import React, { useState, useEffect } from "react";
import {
  Loader2,
  Heart,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { useUpdateDonationMutation } from "../../../redux/actions/Customer/cartCustomer";
import { ImagePath } from "../../../assests";

interface DonateCardProps {
  options?: number[];
  onSelect?: (amount: number) => void;
  isActive?: boolean;
  currentAmount?: number;
}

const DonateCard: React.FC<DonateCardProps> = ({
  options = [5, 10, 20, 50],
  onSelect,
  isActive = false,
  currentAmount = 0,
}) => {
  const [selectedAmount, setSelectedAmount] =
    useState<number | null>(
      currentAmount || null
    );

  const [
    updateDonationMutation,
    { isLoading: isMutatingDonation },
  ] = useUpdateDonationMutation();

  useEffect(() => {
    setSelectedAmount(
      currentAmount || null
    );
  }, [currentAmount]);

  const handleSelectAmount = async (
    amount: number
  ) => {
    const isRemoval =
      selectedAmount === amount;

    const targetAmount =
      isRemoval ? 0 : amount;

    setSelectedAmount(
      isRemoval ? null : amount
    );

    try {
      await updateDonationMutation({
        isDonationEnabled: !isRemoval,
        donationAmount: targetAmount,
      }).unwrap();

      onSelect?.(targetAmount);
    } catch (err) {
      console.error(err);

      setSelectedAmount(
        currentAmount || null
      );
    }
  };

  const hasDonated =
    selectedAmount !== null &&
    selectedAmount > 0;

  return (
    <div
      className="
        relative
        w-full
        overflow-hidden

        rounded-2xl

        border
        border-slate-200

        bg-white

        p-5
        max-[639px]:p-2.5

        shadow-sm

        transition-all
        duration-300
      "
    >
      {/* =====================================================
          LOADING OVERLAY
      ===================================================== */}
      {isMutatingDonation && (
        <div
          className="
            absolute
            inset-0
            z-20

            flex
            items-center
            justify-center

            bg-white/70
            backdrop-blur-[1px]
          "
        >
          <Loader2
            size={18}
            className="
              animate-spin
              text-[#2F5E2E]

              max-[639px]:w-4
              max-[639px]:h-4
            "
          />
        </div>
      )}

      {/* =====================================================
          HEADER
      ===================================================== */}
      <div
        className="
          mb-3.5
          max-[639px]:mb-2.5

          flex
          items-center
          justify-between
        "
      >
        <div
          className="
            flex
            items-center

            gap-2.5
            max-[639px]:gap-1.5

            min-w-0
          "
        >
          {/* ICON */}
          <div
            className="
              flex

              h-8
              w-8

              max-[639px]:h-6
              max-[639px]:w-6

              shrink-0

              items-center
              justify-center

              rounded-lg

              bg-gradient-to-br
              from-[#2F5E2E]
              to-emerald-600

              shadow-xs
            "
          >
            <Heart
              size={16}
              className="
                fill-white/20
                text-white

                max-[639px]:w-3.5
                max-[639px]:h-3.5
              "
            />
          </div>

          {/* TITLE */}
          <div className="min-w-0">
            <h2
              className="
                flex
                items-center

                gap-1.5
                max-[639px]:gap-1

                text-sm
                max-[639px]:text-[11px]

                font-bold
                leading-tight

                text-slate-900
              "
            >
              <span className="truncate">
                {hasDonated
                  ? "Thank you for helping!"
                  : "Feed a Homeless Stray"}
              </span>

              {hasDonated ? (
                <CheckCircle2
                  size={14}
                  className="
                    shrink-0
                    text-emerald-600

                    max-[639px]:w-3
                    max-[639px]:h-3
                  "
                />
              ) : (
                <Sparkles
                  size={14}
                  className="
                    shrink-0

                    fill-amber-500
                    text-amber-500

                    max-[639px]:w-3
                    max-[639px]:h-3
                  "
                />
              )}
            </h2>

            <p
              className="
                mt-0.5

                text-xs
                max-[639px]:text-[9px]

                leading-tight

                text-slate-500

                truncate
              "
            >
              {hasDonated
                ? "Your contribution makes a huge difference"
                : "Add a small donation to feed a stray pet"}
            </p>
          </div>
        </div>
      </div>

      {/* =====================================================
          MEDIA + CONTENT
      ===================================================== */}
      <div
        className={`
          flex
          items-center

          gap-3.5
          max-[639px]:gap-2

          rounded-xl

          border

          p-3.5
          max-[639px]:p-2

          ${
            hasDonated
              ? "border-emerald-100 bg-emerald-50/40"
              : "border-slate-100 bg-slate-50/50"
          }
        `}
      >
        {/* =================================================
            IMAGE
        ================================================= */}
        <div
          className="
            relative

            h-16
            w-16

            max-[639px]:h-12
            max-[639px]:w-12

            sm:h-20
            sm:w-20

            shrink-0

            overflow-hidden

            rounded-xl

            border
            border-slate-200/60

            bg-white

            shadow-xs
          "
        >
          <img
            src={
              hasDonated
                ? ImagePath.AfterDonation ||
                  "image_87927f.jpg"
                : ImagePath.BeforeDonation ||
                  "image_87921d.jpg"
            }
            alt="Stray support initiative"
            className="
              h-full
              w-full

              object-cover

              transition-all
              duration-500

              hover:scale-105
            "
          />
        </div>

        {/* =================================================
            CONTENT
        ================================================= */}
        <div
          className="
            min-w-0
            flex-1

            space-y-2.5
            max-[639px]:space-y-1.5
          "
        >
          {/* DESCRIPTION */}
          <p
            className="
              text-xs
              max-[639px]:text-[9px]

              font-medium

              leading-relaxed
              max-[639px]:leading-snug

              text-slate-600

              line-clamp-2
            "
          >
            {hasDonated
              ? "Bowl status: Filled! Thank you for providing fresh meals and support for a rescue animal."
              : "Provide a vital, healthy food bowl pack to clear immediate hunger pains for a local rescue pet."}
          </p>

          {/* =================================================
              DONATION OPTIONS
              
              Desktop:
              Keep ALL buttons in one row.

              Mobile:
              Allow wrapping if the screen is too narrow.
          ================================================= */}
          <div
            className="
              flex
              items-center

              gap-2
              max-[639px]:gap-1.5

              flex-nowrap
              max-[639px]:flex-wrap

              w-full
            "
          >
            {options.map((amount) => {
              const isCurrentSelection =
                selectedAmount === amount;

              return (
                <button
                  key={amount}
                  type="button"
                  disabled={
                    isMutatingDonation
                  }
                  onClick={() =>
                    handleSelectAmount(
                      amount
                    )
                  }
                  className={`
                    cursor-pointer

                    shrink-0

                    rounded-lg

                    border

                    px-3.5
                    max-[639px]:px-2

                    py-1.5
                    max-[639px]:py-1

                    font-mono

                    text-xs
                    max-[639px]:text-[9px]

                    font-bold

                    whitespace-nowrap

                    transition-all

                    ${
                      isCurrentSelection
                        ? `
                          scale-[1.02]

                          border-[#2F5E2E]

                          bg-[#2F5E2E]

                          text-white

                          shadow-xs
                        `
                        : `
                          border-slate-200

                          bg-white

                          text-slate-700

                          hover:border-slate-300

                          hover:bg-slate-50
                        `
                    }

                    disabled:cursor-not-allowed
                    disabled:opacity-60
                  `}
                >
                  AED {amount}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonateCard;